import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { browserHistory } from 'react-router';

import { createStyles, Divider, Theme, withStyles, WithStyles } from '@material-ui/core';
import { DescriptionOutlined as DescriptionIcon } from '@material-ui/icons';
import { concat, flatMap, get, isEmpty, map, reduce } from 'lodash';
import moment from 'moment';
import { usePatientFormById } from 'requests/resources';

import { PatientFormQuestion } from '@lib/resources/types';

import Question, { isQuestionVisible } from 'containers/PatientIntakeFormAnswersPage/Question';

import { IconButton } from './IconButton';
import { Tooltip } from './Tooltip';
import { Typography } from './Typography';

const styles = (theme: Theme) =>
  createStyles({
    messageContent: {
      color: 'inherit',
      fontWeight: 'inherit',
      width: '100%',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      wordWrap: 'break-word',
      '& a': {
        color: 'inherit !important',
      },
    },
    details: {
      marginBottom: theme.spacing.unit,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
    },
  });

const messages = defineMessages({
  formName: {
    id: 'components.v2.ChatMessagePatientFormContent.formName',
    defaultMessage: 'LumaBot flow name',
  },
  formId: {
    id: 'components.v2.ChatMessagePatientFormContent.formId',
    defaultMessage: 'LumaBot flow ID',
  },
  createdAt: {
    id: 'components.v2.ChatMessagePatientFormContent.createdAt',
    defaultMessage: 'Started',
  },
  completedAt: {
    id: 'components.v2.ChatMessagePatientFormContent.completedAt',
    defaultMessage: 'Ended',
  },
  accessedFrom: {
    id: 'components.v2.ChatMessagePatientFormContent.accessedFrom',
    defaultMessage: 'Accessed From',
  },
  tooltipRedirectForm: {
    id: 'components.v2.ChatMessagePatientFormContent.tooltipRedirectForm',
    defaultMessage: 'View all form responses',
  },
  tooltipRedirectFlow: {
    id: 'components.v2.ChatMessagePatientFormContent.tooltipRedirectFlow',
    defaultMessage: 'View all flow responses',
  },
});

export interface ChatMessagePatientFormContentProps extends WithStyles<typeof styles> {
  patientFormId: string;
}

const ChatMessagePatientFormContent: React.FC<ChatMessagePatientFormContentProps> = ({ classes, patientFormId }) => {
  const { formatMessage } = useIntl();

  const { data: patientForm } = usePatientFormById(patientFormId);

  if (!patientForm) return null;

  const questions = reduce(
    flatMap(get(patientForm.responses, 'pages'), 'questions'),
    (res: PatientFormQuestion[], question) => {
      if (question.type === 'template' || !isQuestionVisible(question)) return res;
      if (question.properties.items) return concat(res, question.properties.items);
      return concat(res, question);
    },
    [],
  );

  if (isEmpty(questions)) return null;

  return (
    <>
      {patientForm.previousPatientForm && (
        <>
          <ChatMessagePatientFormContent classes={classes} patientFormId={patientForm.previousPatientForm} />
          <Divider />
        </>
      )}
      <div className={classes.messageContent}>
        {map(questions, (question) => (
          <Question
            key={question.id}
            variant="lite"
            question={question}
            patientFormId={patientForm._id}
            checkVisibility
          />
        ))}
      </div>
      <div className={classes.details}>
        <div>
          <Typography variant="caption">
            {formatMessage(messages.formName)}: {patientForm.patientFormTemplateName}
          </Typography>
          <Typography variant="caption">
            {formatMessage(messages.formId)}: {patientForm._id}
          </Typography>
          <Typography variant="caption">
            {formatMessage(messages.createdAt)}: {moment(patientForm.createdAt).format('lll')}
          </Typography>
          {patientForm.completedAt && (
            <Typography variant="caption">
              {formatMessage(messages.completedAt)}: {moment(patientForm.completedAt).format('lll')}
            </Typography>
          )}
          <Typography variant="caption">
            {formatMessage(messages.accessedFrom)}: {patientForm.accessedFrom || '-'}
          </Typography>
        </div>
        {patientForm.patient && (
          <Tooltip
            title={formatMessage(patientForm.lumabot ? messages.tooltipRedirectFlow : messages.tooltipRedirectForm)}
            placement="top"
            hideArrow
          >
            <IconButton
              onClick={() => browserHistory.push(`/patients/${patientForm.patient}/formAnswers/${patientForm._id}`)}
            >
              <DescriptionIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </>
  );
};

export default withStyles(styles, { name: 'LumaChatMessagePatientFormContent' })(ChatMessagePatientFormContent);
